import { useEffect, useState } from "react";
import { toast } from "sonner";
import { analyzeNiche } from "@/lib/api";

const VERDICT_STYLES = {
  pursue: { label: "PURSUE", classes: "border-emerald-400 text-emerald-300 bg-emerald-500/10" },
  pursue_with_care: { label: "PURSUE · WITH CARE", classes: "border-amber-400 text-amber-300 bg-amber-500/10" },
  skip: { label: "SKIP", classes: "border-red-400 text-red-300 bg-red-500/10" },
};

function nFmt(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function dur(seconds) {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s (Short)`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export default function NicheAnalysisModal({ niche, open, onClose, onAnalysisComplete }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(niche?.analysis || null);
  const [fromCache, setFromCache] = useState(Boolean(niche?.analysis));

  useEffect(() => {
    if (open) {
      setAnalysis(niche?.analysis || null);
      setFromCache(Boolean(niche?.analysis));
    }
  }, [open, niche]);

  if (!open || !niche) return null;

  const run = async (force = false) => {
    setLoading(true);
    try {
      const r = await analyzeNiche(niche.niche_id, force);
      setAnalysis(r.analysis);
      setFromCache(r.from_cache);
      if (onAnalysisComplete) onAnalysisComplete(niche.niche_id, r.analysis);
      if (force) toast.success("Re-analysed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const verdict = analysis?.verdict ? VERDICT_STYLES[analysis.verdict] : null;

  return (
    <div
      data-testid="niche-analysis-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="min-h-screen flex items-start justify-center p-4 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-4xl bg-zinc-950 border border-zinc-800 rounded-sm my-8">
          {/* Header */}
          <div className="px-6 py-5 border-b border-zinc-800 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-2">
                // NICHE INVESTMENT THESIS
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-white leading-tight">
                {niche.name}
              </h2>
              <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{niche.thesis}</p>
            </div>
            <button
              data-testid="niche-analysis-close-btn"
              onClick={onClose}
              className="font-mono text-sm text-zinc-500 hover:text-yellow-400 px-2 py-1 shrink-0"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {!analysis && !loading && (
              <div className="text-center py-10">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
                  [ AWAITING ANALYSIS ]
                </div>
                <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
                  Claude will read this niche's breakout videos and tell you whether
                  it's worth pursuing — including realistic revenue ranges, posting
                  cadence, and 10 specific title ideas to film first.
                </p>
                <button
                  data-testid="niche-analyze-run-btn"
                  onClick={() => run(false)}
                  className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors"
                >
                  ▸ Analyze niche
                </button>
              </div>
            )}

            {loading && (
              <div className="text-center py-12 font-mono text-sm text-zinc-400">
                <div className="text-yellow-400 mb-2">[recon]</div>
                analysing<span className="blink">_</span>
                <div className="text-xs text-zinc-600 mt-3">
                  This takes 15–30 seconds — Claude reads every comp video.
                </div>
              </div>
            )}

            {analysis && !loading && (
              <div className="space-y-6">
                {/* Verdict */}
                {verdict && (
                  <div className={`border ${verdict.classes} p-4 rounded-sm`}>
                    <div className="font-mono text-xs uppercase tracking-[0.3em] mb-2 font-bold">
                      {verdict.label}
                    </div>
                    <div className="text-sm leading-relaxed">
                      {analysis.verdict_summary}
                    </div>
                  </div>
                )}

                {/* Revenue */}
                {analysis.revenue && (
                  <Section title="Expected monthly revenue">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <RevTile label="Conservative" value={analysis.revenue.low_usd_per_month} />
                      <RevTile label="Typical" value={analysis.revenue.typical_usd_per_month} hi />
                      <RevTile label="Best case" value={analysis.revenue.high_usd_per_month} />
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      {analysis.revenue.reasoning}
                    </p>
                  </Section>
                )}

                {/* Cadence + length */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {analysis.cadence && (
                    <Section title="Posting cadence">
                      <div className="font-display text-3xl font-black text-yellow-400 mb-1">
                        {analysis.cadence.videos_per_week} <span className="text-base text-zinc-400 font-normal">/ week</span>
                      </div>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        {analysis.cadence.reasoning}
                      </p>
                    </Section>
                  )}
                  {analysis.video_length && (
                    <Section title="Video length">
                      <div className="font-display text-3xl font-black text-yellow-400 mb-1">
                        {dur(analysis.video_length.recommended_seconds)}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                        format · {analysis.video_length.format?.replace("_", " ") || "—"}
                      </div>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        {analysis.video_length.reasoning}
                      </p>
                    </Section>
                  )}
                </div>

                {/* Title ideas */}
                {analysis.title_ideas?.length > 0 && (
                  <Section title={`Title ideas (${analysis.title_ideas.length})`}>
                    <ol className="space-y-2" data-testid="title-ideas-list">
                      {analysis.title_ideas.map((t, i) => (
                        <li
                          key={i}
                          className="flex gap-3 items-start text-sm text-zinc-200 border-l-2 border-zinc-800 pl-3 py-1 hover:border-yellow-400 transition-colors"
                        >
                          <span className="font-mono text-yellow-400 text-xs pt-1 shrink-0">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="leading-snug">{t}</span>
                        </li>
                      ))}
                    </ol>
                  </Section>
                )}

                {/* First video blueprint */}
                {analysis.first_video_blueprint && (
                  <Section title="Your first video — blueprint">
                    <div className="text-sm font-semibold text-white mb-3">
                      {analysis.first_video_blueprint.title}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                      Hook · 0:00 – 0:10
                    </div>
                    <p className="text-xs text-zinc-300 mb-4 leading-relaxed">
                      {analysis.first_video_blueprint.hook_seconds_0_to_10}
                    </p>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Structure
                    </div>
                    <ol className="space-y-1 text-xs text-zinc-300 list-decimal list-inside">
                      {(analysis.first_video_blueprint.structure || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  </Section>
                )}

                {/* Risks */}
                {analysis.risks?.length > 0 && (
                  <Section title="Risks to know" tone="warn">
                    <ul className="space-y-2 text-xs text-zinc-300">
                      {analysis.risks.map((r, i) => (
                        <li key={i} className="flex gap-2 leading-relaxed">
                          <span className="text-amber-400 shrink-0">⚠</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <div className="flex items-center gap-3 pt-2 border-t border-zinc-900">
                  <button
                    data-testid="niche-analyze-rerun-btn"
                    onClick={() => run(true)}
                    className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2 rounded-sm transition-colors"
                  >
                    ↻ Re-analyse
                  </button>
                  {fromCache && analysis.generated_at && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      cached · {new Date(analysis.generated_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, tone }) {
  return (
    <div className={`border ${tone === "warn" ? "border-amber-500/30" : "border-zinc-800"} p-4 rounded-sm bg-black`}>
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function RevTile({ label, value, hi = false }) {
  return (
    <div className={`border ${hi ? "border-yellow-400/40 bg-yellow-400/5" : "border-zinc-800"} p-3 rounded-sm`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className={`font-display text-xl font-bold ${hi ? "text-yellow-400" : "text-emerald-300"}`}>
        ${nFmt(value)}<span className="text-zinc-500 text-xs font-normal"> / mo</span>
      </div>
    </div>
  );
}
